import { useState } from "react";
import { Mail, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Newsletter subscription form. Two visual variants:
 *  - variant="light" (default) for use on beige/white sections
 *  - variant="dark" for use on the dark charcoal CTA section
 */
export default function SubscribeForm({ variant = "light", source = "site", title, body }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await api.post("/subscribe", { email, source });
      setDone(true);
      if (data.already_subscribed) {
        toast.success("You're already on the list — thank you!");
      } else {
        toast.success("Welcome! You'll be the first to know.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Could not subscribe");
    } finally {
      setLoading(false);
    }
  };

  const isDark = variant === "dark";

  const heading = title || "Get the latest blogs in your inbox";
  const sub = body || "Tips, mindset and strength training guidance for women — straight from Maya. No spam. Unsubscribe anytime.";

  if (done) {
    return (
      <div
        className={`rounded-[2rem] p-8 sm:p-12 text-center ${
          isDark ? "bg-[#3A3A3A] text-white" : "bg-white border border-[#F3C9D3]/50"
        }`}
        data-testid="subscribe-success"
      >
        <div className={`inline-flex p-3 rounded-full mb-4 ${isDark ? "bg-[#E8A6B8]" : "bg-[#F3C9D3]"}`}>
          <Check size={22} className={isDark ? "text-white" : "text-[#3A3A3A]"} strokeWidth={2} />
        </div>
        <h3 className={`font-serif-display text-3xl mb-2 ${isDark ? "text-white" : "text-[#3A3A3A]"}`}>
          You're <span className="italic text-[#E8A6B8]">in</span>.
        </h3>
        <p className={`text-sm ${isDark ? "text-white/70" : "text-[#5C5C5C]"}`}>
          The next blog post will land in your inbox.
        </p>
      </div>
    );
  }

  return (
    <div
      className={`rounded-[2rem] p-8 sm:p-12 ${
        isDark
          ? "bg-[#3A3A3A] text-white"
          : "bg-white border border-[#F3C9D3]/50"
      }`}
      data-testid="subscribe-block"
    >
      <div className="max-w-xl mx-auto text-center">
        <div
          className={`inline-flex p-3 rounded-full mb-5 ${
            isDark ? "bg-white/10" : "bg-[#F3C9D3]/60"
          }`}
        >
          <Mail
            size={22}
            strokeWidth={1.5}
            className={isDark ? "text-[#E8A6B8]" : "text-[#3A3A3A]"}
          />
        </div>
        <p
          className={`eyebrow mb-3 ${
            isDark ? "text-white/80" : "text-[#3A3A3A]"
          }`}
        >
          Newsletter
        </p>
        <h3
          className={`font-serif-display text-3xl sm:text-4xl leading-tight ${
            isDark ? "text-white" : "text-[#3A3A3A]"
          }`}
        >
          {heading}
        </h3>
        <p
          className={`mt-4 text-sm leading-relaxed ${
            isDark ? "text-white/70" : "text-[#5C5C5C]"
          }`}
        >
          {sub}
        </p>
        <form
          onSubmit={submit}
          className="mt-7 flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
        >
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            data-testid="subscribe-email-input"
            className={`flex-1 rounded-full px-5 py-3.5 text-sm focus:outline-none transition-all ${
              isDark
                ? "bg-white/10 border border-white/15 text-white placeholder:text-white/40 focus:bg-white/15 focus:border-[#E8A6B8]"
                : "bg-[#F6EFE9] border border-[#F3C9D3] text-[#3A3A3A] placeholder:text-[#3A3A3A]/40 focus:border-[#E8A6B8] focus:bg-white"
            }`}
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="subscribe-submit"
            className="btn-pink disabled:opacity-60"
          >
            {loading ? "…" : "Subscribe"}
          </button>
        </form>
      </div>
    </div>
  );
}
